import type { Locale } from "@/context/AppProviders";

export type ModuleItem = {
  title: string;
  desc: string;
};

export type SiteContent = {
  nav: {
    services: string;
    ecommerce: string;
    quickSale: string;
    dealer: string;
    accounting: string;
    website: string;
    pricing: string;
    blog: string;
    contact: string;
    login: string;
    register: string;
  };
  hero: {
    pre: string;
    post: string;
    rotating: string[];
    sub: string;
    cta1: string;
    badge: string;
  };
  stats: { value: number | string; sfx: string; lbl: string }[];
  featT: string;
  featS: string;
  feats: { tag: string; title: string; desc: string; subs: string[] }[];
  modT: string;
  modS: string;
  modTabs: string[];
  mods: { title: string; desc: string; items: ModuleItem[]; badge?: string }[];
  intT: string;
  intS: string;
  blogT: string;
  blogS: string;
  blogMetaTitle: string;
  blogMetaDescription: string;
  posts: { tag: string; date: string; title: string; img: string }[];
  faqT: string;
  faqs: { q: string; paragraphs: string[]; bullets?: string[] }[];
  ctaT: string;
  ctaS: string;
  footerTag: string;
  footerDesc: string;
  footerProduct: string;
  footerServices: string;
  footerResources: string;
  footerCo: string;
  footerContact: string;
  footerCtaTitle: string;
  footerCtaSub: string;
  copyright: string;
  allPosts: string;
  explore: string;
  trustFeatures: { title: string; desc: string; icon: string; iconAlt: string }[];
  productHighlights: {
    onlineBadge: string;
    onlineTitle: string;
    onlineDesc: string;
    onlinePoints: string[];
    onlineImages: {
      desktopHome: string;
      desktopProduct: string;
      mobileHome: string;
      mobileProduct: string;
      mobileList: string;
    };
    onlineImageAlt: {
      desktopHome: string;
      desktopProduct: string;
      mobileHome: string;
      mobileProduct: string;
      mobileList: string;
    };
  };
};

const TR: SiteContent = {
  nav: {
    services: "Servisler",
    ecommerce: "E-Ticaret",
    quickSale: "Hızlı Satış",
    dealer: "Bayi & Toptan",
    accounting: "Ön Muhasebe",
    website: "Web Site",
    pricing: "Fiyatlar",
    blog: "Blog",
    contact: "İletişim",
    login: "Giriş Yap",
    register: "Ücretsiz Deneyin",
  },
  hero: {
    pre: "Tüm",
    post: "Süreçleriniz\nTek Platformda",
    rotating: ["Web", "E-Ticaret", "Ön Muhasebe", "E-Fatura", "Stok", "İş"],
    sub: "Enabase ile online ön muhasebe, fatura, stok, cari hesap ve e-ticaret süreçlerinizi tek bir platformdan yönetin.",
    cta1: "Ücretsiz Deneyin",
    badge: "30 gün ücretsiz · Kredi kartı gerekmez",
  },
  stats: [
    { value: 30, sfx: " Gün", lbl: "Ücretsiz Deneme" },
    { value: "7/24", sfx: "", lbl: "Canlı Destek" },
    { value: 100, sfx: "%", lbl: "Bulut Altyapısı" },
    { value: 1000, sfx: "+", lbl: "Aktif İşletme" },
  ],
  featT: "İşletmenizi Kolayca Yönetin",
  featS: "Finans, e-ticaret, stok ve daha fazlasını tek platform üzerinden kontrol edin.",
  feats: [
    {
      tag: "Finans",
      title: "Ön Muhasebe",
      desc: "Gelir, gider ve cari hesaplarınızı anlık takip edin. Manuel işlem yükünü ortadan kaldırın.",
      subs: ["Gelir & Gider Takibi", "Cari Hesap Yönetimi"],
    },
    {
      tag: "E-Dönüşüm",
      title: "E-Fatura & E-Arşiv",
      desc: "e-fatura ve e-arşiv belgelerinizi tek ekranda hazırlayıp entegratörle otomatik gönderin.",
      subs: ["Otomatik Fatura Gönderimi", "Siparişten Otomatik Fatura"],
    },
    {
      tag: "Satış",
      title: "E-Ticaret Yönetimi",
      desc: "Tüm pazaryerleri ve kendi mağazanızdaki siparişleri tek ekrandan yönetin, kargoya verin.",
      subs: ["Pazaryeri Entegrasyonu", "Sipariş Otomasyonu"],
    },
    {
      tag: "Operasyon",
      title: "Stok Takibi",
      desc: "Ürün stoklarınızı anlık izleyin, kritik stok seviyesinde otomatik uyarı alın.",
      subs: ["Anlık Stok Bildirimleri", "Depo Konum Takibi"],
    },
    {
      tag: "Analitik",
      title: "Raporlama & Analiz",
      desc: "Gelir, gider ve satış verilerinizi interaktif grafiklerle analiz edin, kararlarınızı hızlandırın.",
      subs: ["Detaylı Grafikler", "Özel Rapor Oluşturma"],
    },
    {
      tag: "Ekip",
      title: "Çoklu Kullanıcı",
      desc: "Ekip üyelerinize rol bazlı yetki tanımlayın. Kimin ne yaptığını işlem loglarından takip edin.",
      subs: ["Rol Tanımlamaları", "İşlem Logları"],
    },
    {
      tag: "CRM",
      title: "Müşteri Yönetimi",
      desc: "Müşteri ve bayi bilgilerini, iletişim geçmişini ve satış süreçlerini tek bir yerden yönetin.",
      subs: ["Müşteri Geçmişi", "Satış Süreci Takibi"],
    },
    {
      tag: "Güvenlik",
      title: "Veri Güvenliği",
      desc: "Tüm verileriniz 128-bit SSL ile şifrelenir. Otomatik yedekleme ile veri kaybı yaşamazsınız.",
      subs: ["128-bit SSL Şifreleme", "Otomatik Yedekleme"],
    },
  ],
  modT: "Platformu Keşfedin",
  modS: "Tüm modüller entegre çalışır — ayrı yazılıma ihtiyaç yok.",
  modTabs: ["E-Ticaret", "Hızlı Satış", "Bayi & Toptan", "Ön Muhasebe", "Web Site"],
  mods: [
    {
      title: "Hem Perakende Hem Toptan Satışlarınızı Tek Yerden Yönetin",
      desc: "Bireysel müşterilerinize perakende, bayilerinize toptan fiyatlarla Trendyol, Hepsiburada, N11 ve kendi mağazanızdan tek panelde satış yapın.",
      items: [
        {
          title: "Sipariş & Kargo Yönetimi",
          desc: "Pazaryeri ve web siparişlerini tek listede toplayın; toplu fatura, kargo fişi ve durum güncellemesini aynı panelden yapın.",
        },
        {
          title: "Ürün & Varyant Yönetimi",
          desc: "Katalog, stok, fiyat ve pazaryeri listelerinizi tek merkezden yönetin; Excel ile toplu güncelleme, varyant ve kanal senkronizasyonu.",
        },
        {
          title: "Envanter Takibi",
          desc: "Alış, satış, rezerve ve manuel hareketleri tek tabloda görün; kritik stok uyarıları, Excel raporu ve kâr marjı analizi.",
        },
        {
          title: "Pazaryeri Entegrasyonları",
          desc: "Trendyol, N11, Hepsiburada ve Pazarama: ürün bazında aktarım kontrolü, kategori özellikleri, stok/fiyat gönderimi ve feed anahtarları.",
        },
        {
          title: "Enabase Online Mağaza",
          desc: "Responsive Enabase Online mağaza, SEO araçları, Google Shopping ve Meta feed; katalog verisiyle senkron mağaza.",
        },
        {
          title: "Ödeme & Tahsilat",
          desc: "PayTR, İyzico, havale, kapıda ödeme ve POS; işletmeye tanımlı tüm ödeme kanalları siparişte.",
        },
      ],
    },
    {
      title: "Perakende Satışlarınızı Tek Ekrandan Yönetin",
      desc: "Hızlı Satış POS ekranı: barkod, müşteri analizi, parçalı ödeme, vade kontrolü, anlık stok düşümü ve fiş tahsilat tek kasada.",
      items: [
        {
          title: "Müşteri Analizi",
          desc: "Kasada müşteri seçimi, satın alma geçmişi ve cari borç özeti; satış anında doğru karar.",
        },
        {
          title: "Parçalı Ödeme",
          desc: "Nakit, kart, cari ve tanımlı tüm kanallarla aynı satışta çoklu tahsilat; kalan tutar ekranda.",
        },
        {
          title: "Barkod Okuyucu",
          desc: "USB ve Bluetooth okuyucu ile barkod okutun; SKU ve isim araması aynı alanda.",
        },
        {
          title: "Vade Kontrolü",
          desc: "Vadesi geçmiş borç uyarısı satış öncesi; riskli cari satışı bilinçli yönetin.",
        },
        {
          title: "Anlık Stok Düşümü",
          desc: "Satış onayında envanter düşümü; web ve pazaryeri ile ortak stok havuzu.",
        },
        {
          title: "Fiş & Tahsilat",
          desc: "Satış fişi, yazdırma ve anında tahsilat; gün sonu kasa mutabakatı ve fatura bağlantısı.",
        },
      ],
    },
    {
      title: "Bayilerinize ve Toptan Müşterilerinize Özel Satış Altyapısı",
      desc: "E-ticaret ve bayi kanalı tek altyapıda: 4 fiyat tanımı, bayi/müşteri/grup listeleri, kurumsal portal, online tahsilat ve toplu sipariş.",
      items: [
        {
          title: "Bayi Fiyat Tanımlamaları",
          desc: "Alış, toptan, indirimli ve satış fiyatları; bayi bazlı liste ve iskonto kuralları portalda otomatik.",
        },
        {
          title: "Müşteri Grubu Bazlı Fiyatlar",
          desc: "Segment ve bölge gruplarına ortak fiyat, vade ve limit; müşteri bazlı istisna desteği.",
        },
        {
          title: "Toplu Sipariş Yönetimi",
          desc: "Bayi sipariş onayı, stok rezervasyonu, sevkiyat ve fatura; B2B ve perakende tek panelde.",
        },
        {
          title: "Kurumsal Müşteri Portalı",
          desc: "Enabase ön yüzüyle bayi girişi; özel fiyat, sipariş, fatura görüntüleme ve online cari ödeme. Alt alan adı desteği.",
        },
        {
          title: "Teklif & Onay Süreci",
          desc: "Panelde teklif oluşturma, süreç takibi, PDF çıktı, firmaya e-posta ve siparişe aktarım.",
        },
        {
          title: "Özel Ödeme Vadesi",
          desc: "15, 30, 45 ve 60 gün standart vade; vadesi geçmiş borçta sipariş engeli gibi müşteri kuralları.",
        },
      ],
      badge: "B2B",
    },
    {
      title: "Finansal Süreçlerinizi Otomatikleştirin",
      desc: "Tek ekranda tüm belge türleri, Excel düzeninde giriş, işlem anında cari ve ürün özeti. E-fatura, kasa, banka ve raporlar satışla entegre.",
      items: [
        {
          title: "E-Fatura & E-Arşiv",
          desc: "Belge merkezinden e-fatura ve e-arşiv; entegratör gönderimi, siparişten otomatik belge.",
        },
        {
          title: "Çek & Senet Takibi",
          desc: "Portföy, vade takvimi ve cari bağlantısı; TL, USD, EUR döviz desteği.",
        },
        {
          title: "Kasa & Banka Yönetimi",
          desc: "Kasa ve banka tek ekranda; 15 banka entegrasyonu, tahsilat-fatura eşleştirme.",
        },
        {
          title: "Cari Hesap Yönetimi",
          desc: "Müşteri-tedarikçi ekstre, döviz bakiye; belge girerken anlık cari özeti.",
        },
        {
          title: "Belgeler",
          desc: "Sipariş, fatura, cari ve işlemlere dosya ekleme; ayrı arşiv modülü yok, kayıttan hızlı erişim.",
        },
        {
          title: "Gelir / Gider Analizi",
          desc: "Belgeden otomatik kalemler, grafik ve dönem kıyası; masraf Excel düzeninde.",
        },
        {
          title: "Mali Raporlar",
          desc: "Kâr-zarar, nakit akışı ve satış özeti; tek tık, Excel çıktısı.",
        },
      ],
    },
    {
      title: "Profesyonel Web Sitenizi Kolayca Kurun",
      desc: "Yüksek hızlı blog, etiket ve teknik SEO altyapısı; arama hacmi ve organik sıralama için sağlam temel.",
      items: [
        {
          title: "Sürükle & Bırak Editör",
          desc: "Kod yok; ana sayfa, slider, banner ve kurumsal sayfalar panelden.",
        },
        {
          title: "Blog Yönetimi",
          desc: "Yüksek hızlı blog, sınırsız içerik, etiket ve kategori; arama hacmi için içerik motoru.",
        },
        {
          title: "Kategori & Filtreler",
          desc: "Hiyerarşik kategori, filtreler ve SEO dostu URL; katalog ile tümleşik Enabase Online.",
        },
        {
          title: "Mobil Uyumlu Tasarım",
          desc: "Responsive çıktı, hızlı yükleme; mobil SEO ve dönüşüm odaklı.",
        },
        {
          title: "SEO Araçları",
          desc: "Sitemap, robots, rich snippet, AI meta, önbellek; kendi kendine sıralanabilir teknik temel.",
        },
        {
          title: "Özel Alan Adı Desteği",
          desc: "Kendi domain ve SSL; blog ve mağaza aynı marka otoritesinde.",
        },
      ],
    },
  ],
  intT: "Tümleşik Entegrasyon",
  intS: "Entegre servisler ek ücret olmadan planınıza dahildir ve Enabase bünyesinde dahili olarak çalışır.",
  blogT: "Blog",
  blogS: "Yeni özellikler, operasyon ipuçları ve dijital büyüme içerikleri.",
  blogMetaTitle: "Blog | Ön Muhasebe, e-Fatura ve E-Ticaret",
  blogMetaDescription:
    "Online ön muhasebe, e-fatura, stok ve e-ticaret üzerine Enabase blog yazıları, rehberler ve operasyon ipuçları.",
  posts: [
    {
      tag: "Dijital Pazarlama",
      date: "11 Nis 2025",
      title: "Küresel Pazarlarda E-Ticaret ve Sosyal Medya Stratejileri",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/kuresel-pazarlarda-basari-icin-e-ticaret-ve-sosyal-medya-stratejileriyle-dijital-donusumun-isletmenize-faydalari_67f938c87cfa7.jpg",
    },
    {
      tag: "Dijital Pazarlama",
      date: "11 Nis 2025",
      title: "Küçük İşletmeler İçin Etkili Sosyal Medya Stratejileri",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/kucuk-isletmeler-icin-etkili-sosyal-medya-stratejileri-dijital-pazarlamada-one-cikmanin-yollari_67f93969472c0.jpg",
    },
    {
      tag: "E-Ticaret",
      date: "11 Nis 2025",
      title: "Dijital Pazarlama ve E-Ticarette Muhasebe ile Başarıya Ulaşmanın Yolları",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/dijital-pazarlama-ve-eticaret-dunyasinda-muhasebe-ile-basariya-ulasmanin-yollari_67f939a1aa44e.jpg",
    },
    {
      tag: "E-Ticaret",
      date: "11 Nis 2025",
      title: "Dijital Girişimcilik: Global Pazarlara Erişmenin Yolları",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/dijital-girisimcilik-stratejileri-eticaret-ve-sosyal-medya-ile-global-pazarlara-erisme-yollari_67f93a434fe70.jpg",
    },
    {
      tag: "Girişimcilik",
      date: "11 Nis 2025",
      title: "İhracatta Rekabetçi Avantaj: Dijital Dönüşümün Fırsatları",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/ihracatta-rekabetci-avantaj-dijital-donusum-ve-teknolojinin-girisimcilere-sunduklari-firsatlar_67f93ab457887.jpg",
    },
  ],
  faqT: "Enabase ile İlgili Sıkça Sorulan Sorular",
  faqs: [
    {
      q: "Enabase nedir ve ne işe yarar?",
      paragraphs: [
        "Enabase, KOBİ ve büyüyen işletmelerin satış, stok, ön muhasebe, e-fatura ve Enabase Online'ı tek bulut platformda yönetmesini sağlayan Türkiye odaklı bir iş yazılımıdır.",
        "Ayrı programlar arasında veri kopyalamak yerine sipariş, cari, envanter ve belge kayıtları aynı veri tabanında tutulur. Böylece operasyon ve finans ekipleri aynı gerçeğe bakar.",
      ],
    },
    {
      q: "Enabase'de hangi modüller bir arada çalışır?",
      paragraphs: ["Paket ihtiyacınıza göre modüller açılır; hepsi aynı panel ve aynı müşteri-ürün kayıtlarını paylaşır:"],
      bullets: [
        "E-Ticaret: pazaryeri, online mağaza, sipariş ve kargo",
        "Hızlı Satış: mağaza kasası, barkod ve anlık stok düşümü",
        "Bayi & Toptan: bayi fiyatı, portal ve toplu sipariş",
        "Ön Muhasebe: cari, kasa-banka, çek-senet, gelir-gider",
        "E-Fatura & E-Arşiv: belge hazırlama ve entegratör gönderimi",
        "Web Site: Enabase Online, blog ve SEO odaklı içerik",
      ],
    },
    {
      q: "Enabase'i kimler kullanabilir?",
      paragraphs: [
        "Perakende mağaza, e-ticaret satıcısı, toptan-bayi dağıtımı yapan firma ve ön muhasebesini dijitalleştirmek isteyen KOBİ'ler için uygundur.",
        "Tek kullanıcılı küçük işletmeden çok şubeli yapıya kadar ölçeklenebilir. Yoğun Excel ve WhatsApp ile takip edilen süreçleri tek kayıt sistemine taşımak isteyen ekipler en hızlı faydayı görür.",
      ],
    },
    {
      q: "E-ticaret ve ön muhasebe verisi nasıl paylaşılır?",
      paragraphs: [
        "Sipariş onaylandığında satır, müşteri ve tutar bilgisi cari ve stok tarafına aktarılır. Fatura veya e-belge aynı satış kaydından üretilebilir; çift giriş gerekmez.",
        "Tahsilat, iade ve iptal hareketleri de aynı müşteri kartında izlenir. Ay sonu raporlarında kanal bazlı satış ile kasa ve cari hareketleri birlikte okunur.",
      ],
    },
    {
      q: "Pazaryeri, web mağaza ve mağaza satışı aynı stoktan mı?",
      paragraphs: [
        "Evet. Tek ürün kataloğu ve ortak stok havuzu kullanıldığında Trendyol, Hepsiburada, kendi siteniz veya kasa satışı aynı müsait adedi paylaşır.",
        "Bir kanalda satış diğer kanallardaki stok miktarını günceller. Böylece fazla satış ve manuel stok sayımı ihtiyacı azalır.",
      ],
    },
    {
      q: "Enabase'i kullanmak için teknik bilgi gerekir mi?",
      paragraphs: [
        "Hayır. Arayüz günlük iş dilinde tasarlanmıştır: sipariş listesi, ürün kartı, fatura ekranı ve kasa akışı operasyon ekibinin alışık olduğu mantıkta ilerler.",
        "Kurulumda temel eğitim ve canlı destek ile başlanır. İleri ayarlar (fiyat listesi, vade kuralı, entegratör) adım adım yapılandırılır.",
      ],
    },
    {
      q: "Mevcut ürün ve müşteri listemi aktarabilir miyim?",
      paragraphs: [
        "Evet. Excel ile toplu ürün, stok ve cari aktarımı desteklenir. Mevcut katalog yapınız korunarak panele taşınır.",
        "Pazaryeri ve e-ticaret entegrasyonları canlıya alındığında yeni siparişler otomatik akar. Geçmiş veri aktarımı proje kapsamında planlanır; destek ekibi süreci yönlendirir.",
      ],
    },
    {
      q: "E-fatura için ayrı bir program gerekir mi?",
      paragraphs: [
        "Hayır. E-fatura ve e-arşiv belgeleri Enabase belge merkezinde hazırlanır; E-Logo, Bien, EDM, Mysoft veya Crs Soft gibi entegratörler üzerinden gönderilir.",
        "Sipariş veya mağaza satışından fatura oluşturulabilir. Gönderim durumu, hata mesajı ve cari hareket aynı kayıtta takip edilir.",
      ],
    },
    {
      q: "Kendi alan adımı Enabase'de kullanabilir miyim?",
      paragraphs: [
        "Evet. markaniz.com gibi mevcut alan adınız DNS yönlendirmesiyle Enabase Online mağazanıza bağlanır. Blog ve mağaza aynı domain otoritesinde kalır.",
        "Bayi kanalı için bayi.markaniz.com gibi alt alan adı da kullanılabilir. SSL ile güvenli bağlantı desteklenir.",
      ],
    },
    {
      q: "Ücretsiz deneme süresi nasıl işler?",
      paragraphs: [
        "30 gün boyunca, kredi kartı bilgisi olmadan deneme başlatabilirsiniz. Bu sürede modülleri gerçek iş akışınıza yakın test edebilirsiniz.",
        "Canlı pazaryeri veya entegratör bağlantıları sözleşme onayı sonrası devreye alınır. Deneme bitiminde verileriniz korunur; plan seçerek devam edebilirsiniz.",
      ],
    },
    {
      q: "Kurulum ve destek nasıl sağlanır?",
      paragraphs: [
        "Bulut yapı olduğu için sunucu kurulumu gerekmez; tarayıcıdan giriş yaparak başlarsınız. İlk ürün, cari ve kullanıcı tanımları rehberli ilerler.",
        "7/24 canlı destek, eğitim içerikleri ve servis sayfalarındaki detaylı SSS ile kullanım soruları yanıtlanır. Kritik dönemlerde (kampanya, yıl sonu) operasyon desteği önceliklendirilir.",
      ],
    },
    {
      q: "Fiyatlandırma nasıl belirlenir?",
      paragraphs: [
        "Planlar işletme büyüklüğü, aktif modül ve kullanıcı ihtiyacına göre belirlenir. E-ticaret, muhasebe ve web modüllerini birlikte veya aşamalı alabilirsiniz. Güncel paket fiyatları fiyatlandırma sayfasında şeffaf biçimde yayınlanır.",
        "Süreç boyunca objektif fiyatlandırma uygulanır; sözleşme döneminde farklı bir tutar veya gizli ücret çıkmaz. Özel entegrasyon, ek modül veya kurumsal ihtiyaçlar için satış ekibiyle görüşebilirsiniz; bu talepler ayrıca netleştirilir.",
        "Yıllık ödeme tutarları ekonomik koşullara bağlı olarak güncellenebilir. Paket liste fiyatlarındaki değişiklikler ise dönemsel olarak Enabase yönetimi tarafından belirlenir ve yürürlük tarihi önceden duyurulur.",
      ],
    },
  ],
  ctaT: "Hemen Başlayın",
  ctaS: "30 gün ücretsiz deneyin, kredi kartı gerekmez.",
  footerTag: "Satıştan tahsilata, tek platform.",
  footerDesc:
    "Pazaryeri, web mağaza, kasa ve bayi kanallarınızı stok, cari, e-fatura ve Enabase Online ile aynı panelde yönetin.",
  footerProduct: "Ürün",
  footerServices: "Servisler",
  footerResources: "Kaynaklar",
  footerCo: "Kurumsal",
  footerContact: "İletişim",
  footerCtaTitle: "E-ticaret ve ön muhasebe tek platformda",
  footerCtaSub: "30 gün ücretsiz deneyin · Kredi kartı gerekmez · Tüm modüller dahil",
  copyright: "© 2026 Enabase Tüm Hakları Saklıdır.",
  allPosts: "Tümünü Gör",
  explore: "Keşfet",
  trustFeatures: [
    {
      title: "Otomatik Yedeklemeler",
      desc: "Düzenli, otomatik yedeklemelerle içiniz rahat olsun.",
      icon: "/images/trust/icon_backup.png?v=2",
      iconAlt: "Otomatik yedekleme",
    },
    {
      title: "Ücretsiz Güncellemeler",
      desc: "Çevrimiçi satış yapmanıza yardımcı olmak ve dijital varlığınızı daha da geliştirmek için yararlanabileceğiniz yeni ücretsiz özellikleri sürekli olarak ekliyoruz.",
      icon: "/images/trust/icon_update.png?v=2",
      iconAlt: "Ücretsiz güncellemeler",
    },
    {
      title: "Üst Düzey Güvenlik",
      desc: "128 bit SSL şifreleme, kritik verilerinizin her zaman güvende olmasını sağlar.",
      icon: "/images/trust/icon_sec.png?v=2",
      iconAlt: "Üst düzey güvenlik",
    },
    {
      title: "Hızlı Sunucular",
      desc: "Hızlı ve PCI uyumlu e-ticaret barındırma sunuyoruz.",
      icon: "/images/trust/icon_hiz.png?v=2",
      iconAlt: "Hızlı sunucular",
    },
  ],
  productHighlights: {
    onlineBadge: "Yeni Nesil Platform",
    onlineTitle: "online",
    onlineDesc:
      "Mağazanız, bayi ağınız ve ön muhasebe tek panelde. Servis tabanlı mimariyle modüller birbirine bağlı çalışır — perakende, toptan ve B2B süreçler için hazır.",
    onlinePoints: [
      "Kendi e-ticaret sitenizi dakikalar içinde yayına alın",
      "Bayi ve toptan fiyatlarını tek panelden yönetin",
      "Siparişten faturaya kesintisiz akış",
    ],
    onlineImages: {
      desktopHome: "/images/online/desktop-home.png",
      desktopProduct: "/images/online/desktop-product.png",
      mobileHome: "/images/online/mobile-home.png",
      mobileProduct: "/images/online/mobile-product.png",
      mobileList: "/images/online/mobile-list.png",
    },
    onlineImageAlt: {
      desktopHome: "Enabase Online e-ticaret mağaza ana sayfası",
      desktopProduct: "Enabase Online ürün detay sayfası",
      mobileHome: "Enabase Online mobil mağaza ana sayfası",
      mobileProduct: "Enabase Online mobil ürün sayfası",
      mobileList: "Enabase Online mobil ürün listesi",
    },
  },
};

const EN: SiteContent = {
  nav: {
    services: "Services",
    ecommerce: "E-Commerce",
    quickSale: "Quick Sale",
    dealer: "Dealer & Wholesale",
    accounting: "Accounting",
    website: "Website",
    pricing: "Pricing",
    blog: "Blog",
    contact: "Contact",
    login: "Log In",
    register: "Start Free Trial",
  },
  hero: {
    pre: "All Your",
    post: "Processes,\nOne Platform",
    rotating: ["Web", "E-Commerce", "Accounting", "Invoicing", "Business"],
    sub: "Manage online accounting, invoicing, inventory, current accounts, and e-commerce from a single Enabase platform.",
    cta1: "Start Free Trial",
    badge: "30 days free · No credit card required",
  },
  stats: [
    { value: 30, sfx: " Days", lbl: "Free Trial" },
    { value: "7/24", sfx: "", lbl: "Live Support" },
    { value: 100, sfx: "%", lbl: "Cloud Infrastructure" },
    { value: 1000, sfx: "+", lbl: "Active Businesses" },
  ],
  featT: "Manage Your Business Effortlessly",
  featS: "Control finance, e-commerce, inventory and more from a single platform.",
  feats: [
    {
      tag: "Finance",
      title: "Accounting",
      desc: "Track income, expenses and ledgers in real time. Eliminate manual bookkeeping for good.",
      subs: ["Income & Expense Tracking", "Ledger Management"],
    },
    {
      tag: "E-Transform",
      title: "E-Invoice & E-Archive",
      desc: "Prepare and send e-invoice and e-archive documents from one screen via your integrator.",
      subs: ["Automatic Invoice Submission", "Invoice from Orders"],
    },
    {
      tag: "Sales",
      title: "E-Commerce Management",
      desc: "Manage all marketplace and own-store orders from one screen. Fulfil faster.",
      subs: ["Marketplace Integration", "Order Automation"],
    },
    {
      tag: "Operations",
      title: "Inventory Tracking",
      desc: "Monitor stock levels in real time and get automatic alerts before you run out.",
      subs: ["Real-time Alerts", "Warehouse Location Tracking"],
    },
    {
      tag: "Analytics",
      title: "Reporting & Analytics",
      desc: "Analyse revenue, expenses and sales with interactive charts. Make faster decisions.",
      subs: ["Interactive Charts", "Custom Report Builder"],
    },
    {
      tag: "Team",
      title: "Multi-User Access",
      desc: "Assign role-based permissions to team members and track every action in the audit log.",
      subs: ["Role Definitions", "Audit Logs"],
    },
    {
      tag: "CRM",
      title: "Customer Management",
      desc: "Manage customer and dealer profiles, communication history and sales pipelines in one place.",
      subs: ["Customer History", "Sales Pipeline Tracking"],
    },
    {
      tag: "Security",
      title: "Data Security",
      desc: "All data is encrypted with 128-bit SSL. Automatic backups ensure you never lose anything.",
      subs: ["128-bit SSL Encryption", "Automatic Backups"],
    },
  ],
  modT: "Explore the Platform",
  modS: "All modules work integrated — no separate software needed.",
  modTabs: ["E-Commerce", "Quick Sale", "Dealer & Wholesale", "Accounting", "Website"],
  mods: [
    {
      title: "Sell Retail & Wholesale From One Platform",
      desc: "Sell retail to individual customers and at wholesale prices to dealers — across Trendyol, Hepsiburada, N11 and your own store from one panel.",
      items: [
        {
          title: "Order & Shipping Management",
          desc: "Manage orders and shipping workflows from every channel in one panel.",
        },
        {
          title: "Product & Variant Management",
          desc: "Create and update color, size and attribute variants with ease.",
        },
        {
          title: "Inventory Tracking",
          desc: "Monitor stock levels in real time and get alerts when thresholds are hit.",
        },
        {
          title: "Marketplace Integrations",
          desc: "Keep Trendyol, Hepsiburada, N11 and other marketplaces in sync automatically.",
        },
        {
          title: "Enabase Online Store",
          desc: "Responsive Enabase Online store, SEO tools, Google Shopping and Meta feeds synced with your catalog.",
        },
        {
          title: "Payments & Collection",
          desc: "PayTR, İyzico, bank transfer, cash on delivery and POS; all payment channels on every order.",
        },
      ],
    },
    {
      title: "Manage In-Store Sales From One Screen",
      desc: "Speed up retail with customer analysis, overdue balance checks, split payments and barcode scanning — all from a single POS screen.",
      items: [
        {
          title: "Customer Analysis",
          desc: "Understand customers from purchase history and uncover more sales opportunities.",
        },
        {
          title: "Split Payment",
          desc: "Accept cash, card and other methods in a single transaction.",
        },
        {
          title: "Barcode Reader",
          desc: "Find products by barcode and complete sales in seconds.",
        },
        {
          title: "Credit Control",
          desc: "See overdue balances at checkout and simplify collections.",
        },
        {
          title: "Instant Stock Deduction",
          desc: "Stock updates automatically with every sale, reducing counting errors.",
        },
        {
          title: "Receipt & Collection",
          desc: "Issue receipts and post collections to accounting instantly.",
        },
      ],
    },
    {
      title: "Dealer & Wholesale Sales Infrastructure",
      desc: "Define custom price lists for each dealer or customer group. Automate bulk orders, payment terms and discount rules to grow your wholesale channel.",
      items: [
        {
          title: "Dealer Price Definitions",
          desc: "Set custom price lists and discount rules for each dealer.",
        },
        {
          title: "Customer Group Pricing",
          desc: "Assign different prices and payment terms to wholesale customer groups.",
        },
        {
          title: "Bulk Order Management",
          desc: "Approve bulk dealer orders and plan stock and fulfillment.",
        },
        {
          title: "Corporate Customer Portal",
          desc: "Give dealers a dedicated portal to order at their own prices.",
        },
        {
          title: "Quote & Approval Workflow",
          desc: "Create quotes in the panel, track status, export PDF, email the company, and convert to orders.",
        },
        {
          title: "Custom Payment Terms",
          desc: "Define terms and limits per dealer to keep collections under control.",
        },
      ],
      badge: "B2B",
    },
    {
      title: "Automate Your Financial Processes",
      desc: "Automatically process e-invoices, checks, cash and bank transactions.",
      items: [
        {
          title: "E-Invoice & E-Archive",
          desc: "Generate invoices automatically, send them electronically and manage e-archive processes.",
        },
        {
          title: "Check & Promissory Notes",
          desc: "Track due dates and keep payment and collection plans visible.",
        },
        {
          title: "Cash & Bank Management",
          desc: "Monitor cash and bank movements from one screen and stay reconciled.",
        },
        {
          title: "Ledger Management",
          desc: "View customer and supplier balances in real time and generate statements.",
        },
        {
          title: "Documents",
          desc: "Attach files to orders, invoices, ledgers and transactions; quick access from each record.",
        },
        {
          title: "Revenue / Expense Analytics",
          desc: "Analyze income and expense lines with charts to make faster decisions.",
        },
        {
          title: "Financial Reports",
          desc: "Generate P&L and cash flow reports for any period in one click.",
        },
      ],
    },
    {
      title: "Build Your Professional Website Easily",
      desc: "Fast blog, tag and technical SEO infrastructure built for organic search growth.",
      items: [
        {
          title: "Drag & Drop Editor",
          desc: "No code: homepage, sliders, banners and corporate pages from the panel.",
        },
        {
          title: "Blog Management",
          desc: "High-speed blog, unlimited posts, tags and categories for organic traffic.",
        },
        {
          title: "Categories & Filters",
          desc: "Category tree, filters and SEO-friendly URLs synced with your catalog.",
        },
        {
          title: "Mobile-Responsive Design",
          desc: "Responsive output and fast loading; mobile SEO and conversion focused.",
        },
        {
          title: "SEO Tools",
          desc: "Sitemap, robots, rich snippets, AI meta, caching; solid technical SEO foundation.",
        },
        {
          title: "Custom Domain Support",
          desc: "Your domain and SSL; blog and store under one brand authority.",
        },
      ],
    },
  ],
  intT: "Integrated Integrations",
  intS: "Integrated services are included in your plan at no extra cost and run natively within Enabase.",
  blogT: "Blog",
  blogS: "New features, operation tips, and digital growth content.",
  blogMetaTitle: "Blog | Accounting, E-Invoicing & E-Commerce",
  blogMetaDescription:
    "Enabase blog posts on online accounting, e-invoicing, inventory, and e-commerce — guides and operations tips.",
  posts: [
    {
      tag: "Digital Marketing",
      date: "Apr 11, 2025",
      title: "E-Commerce & Social Media Strategies for Global Markets",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/kuresel-pazarlarda-basari-icin-e-ticaret-ve-sosyal-medya-stratejileriyle-dijital-donusumun-isletmenize-faydalari_67f938c87cfa7.jpg",
    },
    {
      tag: "Digital Marketing",
      date: "Apr 11, 2025",
      title: "Effective Social Media Strategies for Small Businesses",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/kucuk-isletmeler-icin-etkili-sosyal-medya-stratejileri-dijital-pazarlamada-one-cikmanin-yollari_67f93969472c0.jpg",
    },
    {
      tag: "E-Commerce",
      date: "Apr 11, 2025",
      title: "Achieving Success with Accounting in Digital Marketing & E-Commerce",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/dijital-pazarlama-ve-eticaret-dunyasinda-muhasebe-ile-basariya-ulasmanin-yollari_67f939a1aa44e.jpg",
    },
    {
      tag: "E-Commerce",
      date: "Apr 11, 2025",
      title: "Digital Entrepreneurship: Reaching Global Markets",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/dijital-girisimcilik-stratejileri-eticaret-ve-sosyal-medya-ile-global-pazarlara-erisme-yollari_67f93a434fe70.jpg",
    },
    {
      tag: "Entrepreneurship",
      date: "Apr 11, 2025",
      title: "Competitive Edge in Export: Digital Transformation Opportunities",
      img: "https://cdn.enabase.com/images/blog-gorsel/ENABASE/ihracatta-rekabetci-avantaj-dijital-donusum-ve-teknolojinin-girisimcilere-sunduklari-firsatlar_67f93ab457887.jpg",
    },
  ],
  faqT: "Frequently Asked Questions About Enabase",
  faqs: [
    {
      q: "What is Enabase and what does it do?",
      paragraphs: [
        "Enabase is Turkey-focused cloud business software that lets growing companies run sales, inventory, pre-accounting, e-invoicing, and their Enabase Online store from one platform.",
        "Instead of copying data between separate tools, orders, ledgers, stock, and documents share one database so operations and finance teams work from the same source of truth.",
      ],
    },
    {
      q: "Which modules work together on Enabase?",
      paragraphs: ["Modules are enabled per plan; all share the same panel, customers, and product catalog:"],
      bullets: [
        "E-Commerce: marketplaces, online store, orders, and shipping",
        "Quick Sale: in-store POS, barcode scanning, real-time stock deduction",
        "Dealer & Wholesale: dealer pricing, portal, and bulk orders",
        "Pre-Accounting: ledgers, cash and bank, checks and notes, P&L",
        "E-Invoice & E-Archive: document preparation and integrator submission",
        "Website: Enabase Online, blog, and SEO-focused content",
      ],
    },
    {
      q: "Who is Enabase for?",
      paragraphs: [
        "Retail stores, online sellers, wholesale and dealer networks, and SMEs digitizing day-to-day finance are a strong fit.",
        "It scales from a single user to multi-branch setups. Teams that today rely on spreadsheets and chat threads for tracking usually see the fastest improvement.",
      ],
    },
    {
      q: "How do e-commerce and pre-accounting share data?",
      paragraphs: [
        "When an order is confirmed, line items, customer, and totals flow into ledger and inventory. Invoices or e-documents can be created from the same sale without re-entry.",
        "Collections, returns, and cancellations stay on the customer record. Month-end reports can read channel sales alongside cash and ledger movements.",
      ],
    },
    {
      q: "Do marketplace, web store, and POS share one stock pool?",
      paragraphs: [
        "Yes. With a single catalog and shared stock pool, marketplace channels, your own site, and in-store sales use the same available quantity.",
        "A sale in one channel updates availability everywhere else, reducing overselling and manual stock reconciliation.",
      ],
    },
    {
      q: "Do I need technical skills to use Enabase?",
      paragraphs: [
        "No. The interface follows everyday business language: order lists, product cards, invoice screens, and checkout flows feel familiar to operations teams.",
        "You start with onboarding and live support. Advanced setup (price lists, payment terms, integrators) is configured step by step.",
      ],
    },
    {
      q: "Can I import my existing products and customers?",
      paragraphs: [
        "Yes. Bulk import via Excel is supported for products, stock, and ledgers. Your current catalog structure can be carried into the panel.",
        "After go-live, new marketplace and e-commerce orders flow automatically. Historical migration is planned per project with guidance from support.",
      ],
    },
    {
      q: "Do I need separate software for e-invoicing?",
      paragraphs: [
        "No. E-invoice and e-archive documents are prepared in Enabase and sent through integrators such as E-Logo, Bien, EDM, Mysoft, or Crs Soft.",
        "Invoices can be generated from orders or in-store sales. Submission status, errors, and ledger impact are tracked on the same record.",
      ],
    },
    {
      q: "Can I use my own domain with Enabase?",
      paragraphs: [
        "Yes. Your existing domain (e.g. yourbrand.com) connects to Enabase Online via DNS. Blog and shop stay under one domain authority.",
        "A subdomain such as b2b.yourbrand.com is available for dealer channels. Secure HTTPS is supported.",
      ],
    },
    {
      q: "How does the free trial work?",
      paragraphs: [
        "You can start a 30-day trial with no credit card. Use the period to test modules close to your real workflows.",
        "Live marketplace and integrator connections go live after agreement approval. Your data remains after the trial; choose a plan to continue.",
      ],
    },
    {
      q: "How are setup and support provided?",
      paragraphs: [
        "As a cloud product, there is no server setup; you sign in from the browser. Initial products, ledgers, and users are set up with guided steps.",
        "24/7 live support, learning resources, and detailed FAQs on service pages answer day-to-day questions. Peak periods (campaigns, year-end) are prioritized.",
      ],
    },
    {
      q: "How is pricing determined?",
      paragraphs: [
        "Plans depend on business size, active modules, and user needs. You can adopt e-commerce, accounting, and web modules together or in phases. Current package prices are published transparently on the pricing page.",
        "Pricing stays objective for the term of your agreement: no surprise amounts or hidden fees mid-contract. For custom integrations, extra modules, or enterprise needs, talk to sales; those items are quoted separately and clearly.",
        "Annual payment amounts may be updated in line with economic conditions. Changes to published package list prices are set periodically by Enabase management and announced before they take effect.",
      ],
    },
  ],
  ctaT: "Get Started Today",
  ctaS: "30 days free, no credit card required.",
  footerTag: "From sale to collection, one platform.",
  footerDesc:
    "Run marketplace, web store, POS, and dealer channels with stock, ledgers, e-invoicing, and Enabase Online in one panel.",
  footerProduct: "Product",
  footerServices: "Services",
  footerResources: "Resources",
  footerCo: "Company",
  footerContact: "Contact",
  footerCtaTitle: "E-commerce and accounting on one platform",
  footerCtaSub: "30-day free trial · No credit card · All modules included",
  copyright: "© 2026 Enabase. All Rights Reserved.",
  allPosts: "View All",
  explore: "Explore",
  trustFeatures: [
    {
      title: "Automatic Backups",
      desc: "Rest easy with regular, automatic backups.",
      icon: "/images/trust/icon_backup.png?v=2",
      iconAlt: "Automatic backups",
    },
    {
      title: "Free Updates",
      desc: "We continuously add new free features to help you sell online and grow your digital presence.",
      icon: "/images/trust/icon_update.png?v=2",
      iconAlt: "Free updates",
    },
    {
      title: "Top-Level Security",
      desc: "128-bit SSL encryption keeps your critical data safe at all times.",
      icon: "/images/trust/icon_sec.png?v=2",
      iconAlt: "Top-level security",
    },
    {
      title: "Fast Servers",
      desc: "We offer fast, PCI-compliant e-commerce hosting.",
      icon: "/images/trust/icon_hiz.png?v=2",
      iconAlt: "Fast servers",
    },
  ],
  productHighlights: {
    onlineBadge: "Next-Gen Platform",
    onlineTitle: "online",
    onlineDesc:
      "Your store, dealer network and accounting in one panel. Service-based modules work together — ready for retail, wholesale and B2B.",
    onlinePoints: [
      "Launch your own Enabase Online store in minutes",
      "Manage dealer and wholesale pricing in one place",
      "Seamless flow from order to invoice",
    ],
    onlineImages: {
      desktopHome: "/images/online/desktop-home.png",
      desktopProduct: "/images/online/desktop-product.png",
      mobileHome: "/images/online/mobile-home.png",
      mobileProduct: "/images/online/mobile-product.png",
      mobileList: "/images/online/mobile-list.png",
    },
    onlineImageAlt: {
      desktopHome: "Enabase Online store homepage",
      desktopProduct: "Enabase Online product detail page",
      mobileHome: "Enabase Online mobile store homepage",
      mobileProduct: "Enabase Online mobile product page",
      mobileList: "Enabase Online mobile product listing",
    },
  },
};

export const SITE_CONTENT: Record<Locale, SiteContent> = { tr: TR, en: EN };

export { INTEGRATIONS, getIntegrations } from "@/content/integrations";
export type { IntegrationCopy, IntegrationItem } from "@/content/integrations";

export const FOOTER_PRODUCT_LINKS = [
  ["Özellikler", "Features", "/ozellikler"],
  ["Fiyatlandırma", "Pricing", "/fiyatlandirma"],
  ["Entegrasyonlar", "Integrations", "/entegrasyonlar"],
] as const;

export const FOOTER_SERVICE_LINKS = [
  ["E-Ticaret", "E-Commerce", "/servisler/e-ticaret"],
  ["Hızlı Satış", "Quick Sale", "/servisler/hizli-satis"],
  ["Bayi & Toptan", "Dealer & Wholesale", "/servisler/bayi-toptan"],
  ["Ön Muhasebe", "Accounting", "/servisler/on-muhasebe"],
  ["Web Site", "Website", "/servisler/web-site"],
] as const;

export const FOOTER_RESOURCE_LINKS = [
  ["Blog", "Blog", "/blog"],
  ["İletişim", "Contact", "/iletisim"],
  ["SSS", "FAQ", "/#faq"],
] as const;

export const FOOTER_BOTTOM_LINKS = [
  ["Hakkımızda", "About", "/hakkimizda"],
  ["Gizlilik Politikası", "Privacy Policy", "/gizlilik"],
  ["Kullanım Sözleşmesi", "Terms of Use", "/kullanim-sozlesmesi"],
] as const;

export const FOOTER_CO_LINKS = [
  ["Hakkımızda", "About", "/hakkimizda"],
  ["Blog", "Blog", "/blog"],
  ["İletişim", "Contact", "/iletisim"],
  ["Gizlilik Politikası", "Privacy Policy", "/gizlilik"],
  ["Kullanım Sözleşmesi", "Terms", "/kullanim-sozlesmesi"],
] as const;

export function useSiteContent(locale: Locale): SiteContent {
  return SITE_CONTENT[locale];
}
